import axios from 'axios'
import { trackingStatusItemsToRows } from '@/components/StatusMonitor/tracking-status-rows'
import { STATUS_ALL, STATUS_ARRIVED, STATUS_INCOMING } from '@/constants/shared-constants'
import { statusMonitorSearchHistory } from '@/utils/search-history'
import { MONITOR_SEARCH_INPUTS, MONITOR_SEARCH_SUGGESTIONS } from './status-monitor/search-inputs'

const DEFAULT_MAX_RESULTS = 20

function validStatus (status) {
  const allowedStatuses = [STATUS_ALL, STATUS_ARRIVED, STATUS_INCOMING]
  return allowedStatuses.includes(status)
}

/**
 * List Tracking Statuses from Parts API
 *
 * @param {Object} queryParameters
 * @param {String|null} queryParameters.query - Free text search string. If empty or null,
 *   the Tracking Statuses are listed without using document search in the backend.
 * @param {String} queryParameters.status - Status of tracking. @see 'shared-constants.js'
 * @param {Boolean} queryParameters.myOrders - Include only user's own orders.
 * @param {Number} queryParameters.maxResults - (Integer) Limit how many items are at most
 *   in the response.
 * @param {number} queryParameters.page (Integer) current page index in stock items array. Add one to get the database page.
 */
function listTrackingStatuses (queryParameters) {
  const uid = 'me' // UID is currently not used in listing.
  const url = `${process.env.VUE_APP_PARTS_API_ENDPOINT}/parts/v1/users/${uid}/tracking-status`
  const parameters = ['query', 'page', 'status', 'myOrders', 'maxResults']
  parameters.forEach((parameter) => {
    // Check that caller has not forgot to include required parameter.
    if (queryParameters[parameter] === undefined) {
      throw Error(`Missing parameter ${parameter} in listTrackingStatuses`)
    }
  })
  if (!validStatus(queryParameters.status)) {
    throw Error(`Invalid value "${queryParameters.status}" for parameter status`)
  }
  const requestPromise = axios.get(url, { params: queryParameters })
    .catch((error) => {
      console.error({ _: 'listTrackingStatuses failed', queryParameters, url, error })
      throw error
    })
  return requestPromise
}

/**
 * @typedef {object} TrackingStatusesPage
 * @description A page of tracking status results.
 * @property {boolean} hasNext - Whether there are more pages.
 * @property {boolean} hasPrev - Whether there are pages before this one.
 * @property {number} index - Page number starting from 0.
 * @property {number} maxResults - Maximum number of results in a single page.
 * @property {boolean} myOrders - Whether the query is only for the user's orders.
 * @property {number} page - Page number starting from 1.
 * @property {number} pages - The total number of pages.
 * @property {string} query - The search query.
 * @property {string} status - Specifies the value of the "pending" parameter.
 * @property {object} extraParams - Additional search parameters that were passed to the search.
 * @property {object[]} trackingStatus - Tracking Status (legacy) parent items from the response.
 */

export default {
  namespaced: true,
  state: {
    userSearchOptions: {
      status: STATUS_ALL,
      myOrders: true,
      maxResults: DEFAULT_MAX_RESULTS
    },
    searchText: null, // This stores the user submitted free text search.
    selectedPage: 0,
    ongoingQuery: false,

    /** @type {TrackingStatusesPage[]} */
    currentPages: [],
    firstFetchDone: false, // True after the first Tracking Status listing request has been made
    searchArray: [],
    searchDict: {},
    errorIndexes: []
  },
  getters: {
    /**
     * Return all Tracking Status items from all fetched pages for mobile view.
     *
     * Uses pre-computed `transformedRows` stored at write time in the
     * `addTrackingStatusPage` mutation, eliminating the deep clone and
     * transformation that previously ran on every getter access.
     *
     * @param {Object} state - Vuex state for getters.
     * @returns {Array} - List of enriched and restructured Tracking Status
     *   items to be used in the Mobile view.
     */
    materialGroupTrackingStatuses: (state) => {
      const allRows = []
      for (const page of state.currentPages) {
        allRows.push(...page.transformedRows)
      }
      return allRows
    },
    /**
     * Return transformed Tracking Status rows for a specific page.
     *
     * Uses pre-computed `transformedRows` stored at write time.
     *
     * @param {Object} state - Vuex state for getters.
     * @returns {Function} - Function accepting page index, returns transformed rows.
     */
    trackingStatusPage: (state) => (page) => {
      if (page !== parseInt(page)) {
        throw Error(`Parameter "page" must be integer. Was "${page}".`)
      }
      if (page < 0) {
        throw Error(`Parameter "page" must be 0 or more. Was "${page}".`)
      }
      if ((state.currentPages?.length ?? 0) === 0) {
        throw Error('Tried to fetch a page when there are no pages in currentPages.')
      }
      if (page > state.currentPages.length - 1) {
        throw Error(`Index of fetched page ${page} must smaller than current pages length ${state.currentPages.length}.`)
      }
      return state.currentPages[page].transformedRows
    },
    /**
     * Return the last page of Tracking Status items
     *
     * @param {Object} state - Vuex state for getters.
     * @returns {TrackingStatusesPage|null} -
     *   Page object with raw Tracking Status items from the response and
     *   additional metadata. This is the last/latest fetched page or in case
     *   where there are no pages, `null`. Note that a search result with no
     *   items is still a page with no items. One situation where there are no
     *   Tracking Status pages is when the page is loaded and the first request
     *   (search) has not returned anything.
     */
    lastPage: (state) => {
      if (state.currentPages.length === 0) {
        return null
      }
      const lastPage = state.currentPages[state.currentPages.length - 1]
      return lastPage
    },
    selectedPage: (state) => {
      return state.selectedPage
    },
    /**
     * Indicator if a request can be made to fetch more Tracking Status items
     *
     * @param {Object} state - Vuex state for getters.
     * @param {Object} getters - Vuex getters.
     * @returns {boolean} - True if there are more pages.
     *   FIXME: Exception for this rule is that this will also return true if there are
     *   no pages at all. This means that the first request when the page is loaded has
     *   not returned any results. This might be used in those situations to trigger
     *   the inital search request. It would be better to check separately if the first
     *   fetch has not been made.
     * @see firstFetchDone
     */
    hasNextPage: (state, getters) => {
      const lastPage = getters.lastPage
      if (lastPage === null) {
        // Placeholder if nothing is loaded
        // FIXME: Remove this check and use pages and/or other means to determine
        // if the initial fetch in page load must be done.
        return true
      }
      if (lastPage?.hasNext) {
        return true
      }
      return false
    },
    /**
     * Indicates if front-end is currently calling Parts API to list Tracking Statuses
     *
     * @param {Object} state - Vuex state for getters.
     * @returns {Boolean} - True if request has been made but response has not arrived.
     */
    ongoingQuery: (state) => {
      return state.ongoingQuery
    },
    /**
     * Has there been at least one call to Parts API for listing Tracking Statuses
     *
     * This helps to determine when the page is loaded and search should or should not be
     * made without user action.
     *
     * @param {Object} state - Vuex state for getters.
     * @returns {Boolean} - True after first request to Parts API Tracking Statuses is finished.
     */
    firstFetchDone: (state) => {
      return state.firstFetchDone
    },
    searchText: (state) => {
      return state.searchText
    },
    userSearchOptions: (state) => {
      return state.userSearchOptions
    },
    searchSuggestionsList: (state) => {
      return MONITOR_SEARCH_SUGGESTIONS
    },
    allowedSearchInputs: (state) => {
      return MONITOR_SEARCH_INPUTS
    },
    searchArray: (state) => {
      return state.searchArray
    },
    searchDict: (state) => {
      return state.searchDict
    },
    errorIndexes: (state) => {
      return state.errorIndexes
    }
  },
  mutations: {
    addTrackingStatusPage (state, { queryParameters, response }) {
      const matchingQueryPages = state.currentPages.filter((page) => page.query === queryParameters.query)
        .filter((page) => page.status === queryParameters.status)
        .filter((page) => page.myOrders === queryParameters.myOrders)
        .filter((page) => page.maxResults === queryParameters.maxResults)
      if (matchingQueryPages.length > 0) {
        // There is a query and pagination in process with those queryParameters.
        // Now let's see if the page index has already been cached.
        const matchingPage = matchingQueryPages.find(page => page.page === queryParameters.page)
        if (matchingPage) {
          // Page already exists in cache — skip silently.
          // This can happen when a prefetch and a user-triggered fetch race
          // for the same page (KPT-1010). The first to arrive wins.
          return
        }
      } else {
        // No matching search query found, so let's clear the page cache
        state.currentPages = []
      }

      // Extract extra parameters by removing the standard ones
      const standardParams = ['query', 'page', 'status', 'myOrders', 'maxResults']
      const extraParams = {}
      Object.keys(queryParameters).forEach(key => {
        if (!standardParams.includes(key)) {
          extraParams[key] = queryParameters[key]
        }
      })

      // Add the page to the list. If query matched this will append to the existing list.
      const newPage = {
        index: state.currentPages.length,
        query: queryParameters.query,
        status: queryParameters.status,
        myOrders: queryParameters.myOrders,
        maxResults: queryParameters.maxResults,
        extraParams,
        page: response.data.page,
        pages: response.data.pages,
        hasNext: response.data.has_next,
        hasPrev: response.data.has_prev,
        trackingStatus: response.data.trackingStatus,
        // Pre-compute the transformed rows once at write time.
        // Previously this transformation (including a JSON.parse/JSON.stringify
        // deep clone) ran inside the getter on every access.
        transformedRows: trackingStatusItemsToRows(response.data.trackingStatus)
      }
      state.currentPages.push(newPage)
      // Set the current page to point to the newly added page — but NOT for
      // prefetched pages, which should be cached silently without switching
      // the user's view. Components don't have to respect this value.
      if (!queryParameters._isPrefetch) {
        state.selectedPage = newPage.index
      }
    },
    clearCurrentPages (state) {
      state.currentPages = []
      state.selectedPage = null
    },
    startQuery (state) {
      state.ongoingQuery = true
    },
    /**
     * Batched mutation that atomically clears pages, sets search text, and
     * marks the query as ongoing — all in a single reactivity cycle.
     *
     * This replaces three separate commits (clearCurrentPages + setSearchText +
     * startQuery) that previously triggered three cascading re-renders.
     *
     * @param {Object} state - Vuex state.
     * @param {Object} payload
     * @param {String|null} payload.searchText - The search indicator text.
     */
    startSearch (state, { searchText }) {
      state.currentPages = []
      state.selectedPage = null
      state.searchText = searchText ?? null
      state.ongoingQuery = true
    },
    finishQuery (state) {
      state.ongoingQuery = false
      state.firstFetchDone = true // This is set to false only in the "page load" (default)
    },
    setSearchText (state, searchText) {
      state.searchText = searchText ?? null
    },
    setSearchOptionStatus (state, status) {
      if (!validStatus(status)) {
        throw Error(`Invalid value ${status} for search option "status"`)
      }
      state.userSearchOptions = {
        ...state.userSearchOptions,
        status
      }
    },
    setSearchOptionMyOrders (state, myOrders) {
      if (typeof myOrders !== 'boolean') {
        throw Error(`Invalid value ${myOrders} for search option "myOrders"`)
      }
      state.userSearchOptions = {
        ...state.userSearchOptions,
        myOrders
      }
    },
    setSelectedPage (state, page) {
      if (page === undefined || page === null) {
        throw Error('Missing required parameter "page" in setSelectedPage')
      }
      if (page !== parseInt(page)) {
        // Rudimentary value check
        throw Error(`Invalid value ${page} for page parameter in setSelectedPage`)
      }
      if (page > state.currentPages.length) {
        throw Error(`Provided page value ${page} is out of bound for last page index ${state.currentPages.length}`)
      }
      state.selectedPage = page
    },
    setSearchArray (state, searchArray) {
      state.searchArray = searchArray
    },
    setSearchDict (state, searchDict) {
      state.searchDict = searchDict
    },
    setErrorIndexes (state, errorIndexes) {
      state.errorIndexes = errorIndexes
    }
  },
  actions: {
    /**
     * List Tracking Statuses from Parts API and add results as a new page
     *
     * Use `search` and `fetchNextPage` actions when possible. This is just a wrapper
     * to `listTrackingStatuses` function that sets the `ongoingQuery` flag and adds
     * the results to the Tracking Status collection `currentPages`.
     *
     * @see search
     * @see fetchNextPage
     * @param {ActionContext} [vuexContext]
     * @returns {Promise} - Raw Tracking Status response from the backend
     *  (passed by listTrackingStatuses function)
     */
    fetchPage ({ commit, state, dispatch }, queryParameters) {
      // Only set startQuery if not already started (e.g. by startSearch batch).
      if (!state.ongoingQuery) {
        commit('startQuery')
      }
      let fetchedResponse = null
      const requestPromise = listTrackingStatuses(queryParameters)
        .then((response) => {
          commit('addTrackingStatusPage', { queryParameters, response })
          // Tracking status counts are in global state so we must use root: true.
          // FIXME: Move tracking status counts to this namespace.
          commit('setTrackingStatusCounts', response, { root: true })
          fetchedResponse = response
          return response
        })
        .catch(error => {
          commit('setErrorMessage', error?.response?.data?.message ?? error, { root: true })
        })
        .finally(() => {
          // Clear the loading spinner FIRST
          commit('finishQuery')
          // KPT-1010: THEN prefetch next page in the background.
          // This guarantees the spinner is gone before the prefetch HTTP call starts.
          if (fetchedResponse?.data?.has_next && !queryParameters._isPrefetch) {
            dispatch('prefetchNextPage', queryParameters)
          }
        })
      return requestPromise
    },
    /**
     * Fetch the next page.
     *
     * This function does not take any parameters, but instead reuses the
     * parameters from the initial search.
     *
     * @see listTrackingStatuses
     *
     * @param {ActionContext} [vuexContext]
     * @returns {Promise} - Raw Tracking Status response from the backend
     *  (passed by fetchPage action)
     */
    fetchNextPage ({ state, dispatch, getters, commit }, { maxResults } = {}) {
      if (state.currentPages.length === 0) {
        // No previous pages - this is the initial fetch
        // Use stored searchDict for persistence or empty object for first load
        return dispatch('search', {
          query: null,
          extraParams: state.searchDict || {},
          maxResults
        })
      }

      // Use the currently SELECTED page (what the user is viewing),
      // not lastPage (which may be a prefetched page ahead of the user).
      const currentPageIndex = state.selectedPage ?? 0
      const currentPage = state.currentPages[currentPageIndex]
      if (!currentPage) {
        // Fallback: shouldn't happen, but use lastPage if selectedPage is invalid
        return dispatch('fetchPage', {
          query: getters.lastPage.query,
          page: getters.lastPage.page + 1,
          status: getters.lastPage.status,
          myOrders: getters.lastPage.myOrders,
          maxResults: maxResults ?? getters.lastPage.maxResults,
          ...getters.lastPage.extraParams
        })
      }

      const nextApiPage = currentPage.page + 1

      // KPT-1010: Check if the next page was already prefetched and cached.
      const cachedPage = state.currentPages.find(
        page => page.page === nextApiPage &&
                page.query === currentPage.query &&
                page.status === currentPage.status &&
                page.myOrders === currentPage.myOrders
      )
      if (cachedPage) {
        // Page already prefetched — just select it, no network request needed.
        commit('setSelectedPage', cachedPage.index)
        // Prefetch the page after this one
        if (cachedPage.hasNext) {
          dispatch('prefetchNextPage', {
            query: cachedPage.query,
            page: cachedPage.page,
            status: cachedPage.status,
            myOrders: cachedPage.myOrders,
            maxResults: maxResults ?? cachedPage.maxResults,
            ...cachedPage.extraParams
          })
        }
        return Promise.resolve()
      }

      // Not cached — fetch from API
      const queryParameters = {
        query: currentPage.query,
        page: nextApiPage,
        status: currentPage.status,
        myOrders: currentPage.myOrders,
        maxResults: maxResults ?? currentPage.maxResults,
        ...currentPage.extraParams
      }
      return dispatch('fetchPage', queryParameters)
    },
    /**
     * Silently prefetch the next page in the background.
     *
     * This does NOT set ongoingQuery (no loading spinner), and failures
     * are silently ignored — the user simply won't get instant next-page
     * and will fall back to a normal fetch. See KPT-1010.
     *
     * @param {ActionContext} [vuexContext]
     * @param {Object} currentPageParams - The query parameters of the page that just loaded.
     */
    prefetchNextPage ({ state, commit }, currentPageParams) {
      const nextPageNumber = (currentPageParams.page ?? 1) + 1

      // Don't prefetch if already cached
      const alreadyCached = state.currentPages.find(
        page => page.page === nextPageNumber &&
                page.query === currentPageParams.query &&
                page.status === currentPageParams.status &&
                page.myOrders === currentPageParams.myOrders
      )
      if (alreadyCached) return

      // Build prefetch parameters (strip _isPrefetch flag for the API call)
      const { _isPrefetch, ...cleanParams } = currentPageParams
      const prefetchParams = {
        ...cleanParams,
        page: nextPageNumber
      }

      // Silent fetch — no ongoingQuery flag, no error surfacing
      listTrackingStatuses(prefetchParams)
        .then((response) => {
          // Double-check it wasn't fetched while we were waiting
          const stillNeeded = !state.currentPages.find(
            page => page.page === nextPageNumber &&
                    page.query === prefetchParams.query &&
                    page.status === prefetchParams.status &&
                    page.myOrders === prefetchParams.myOrders
          )
          if (stillNeeded) {
            commit('addTrackingStatusPage', { queryParameters: { ...prefetchParams, _isPrefetch: true }, response })
          }
        })
        .catch(() => {
          // Silently ignore prefetch failures — user will fetch normally on click
        })
    },
    /**
     * Fetch the first page of a search
     *
     * Start a new search and pagination process. If there were results (pages) stored in
     * Vuex, they will be cleared. After the first page is fetched, you can dispatch
     * action `fetchNextPage` to continue getting new items.
     *
     * The search options will be filled in using the `userSearchOptions` if the optional
     * parameters are not provided. This is the preferred use of this action.
     *
     * @see listTrackingStatuses action how the response is handled.
     *
     * Returns a promise with the raw response (passed from fetchPage action). It
     * should be not be necessary to use the data inside the response directly, but it might
     * be useful to see the response metadata such as the response code.
     *
     * @param {ActionContext} [vuexContext]
     * @param {Object} payload
     * @param {String|null} payload.query - Free text search string. If empty or null,
     *   the Tracking Statuses are listed without using document search in the backend.
     * @param {String} [payload.status] - Status of tracking. Default value is using userSearchOptions state.
     *   @see 'shared-constants.js'
     * @param {Boolean} [payload.myOrders] - Include only user's own orders. Default value is using
     *   userSearchOptions state.
     * @param {Number} [payload.maxResults] - (Integer) Limit how many items are at most in the response.
     *   Default value is using userSearchOptions state.
     * @param {Boolean} [payload.saveToSearchHistory=true] - Save free text used for search to local storage.
     * @returns {Promise} - Raw Tracking Status response from the backend
     *  (passed by fetchPage action)
     */
    search (
      { state, getters, commit, dispatch },
      { query, status, myOrders, maxResults, saveToSearchHistory, extraParams = {} } = {}
    ) {
      if (getters.ongoingQuery) {
        // When calling this action check that there is no ongoing query.
        // Duplicate dispatches are dropped gracefully instead of crashing.
        console.warn('statusMonitor/search: dropped — query is still in progress', {
          query,
          status,
          myOrders,
          maxResults
        })
        return
      }
      const queryParameters = {
        query,
        page: 1, // Search can't start with an existing state.
        status: status ?? state.userSearchOptions.status,
        myOrders: myOrders ?? state.userSearchOptions.myOrders,
        maxResults: maxResults ?? state.userSearchOptions.maxResults,
        ...extraParams
      }
      // Compute the search text indicator before batching.
      const searchKeywords = []
      for (const [key, value] of Object.entries(extraParams)) {
        searchKeywords.push(`${key}=${value}`)
      }
      let searchText
      if (query) {
        searchText = [...searchKeywords, query].join(' & ')
      } else {
        searchText = searchKeywords.join(' & ')
      }
      // Batch: clear pages + set search text + mark query ongoing — single commit.
      commit('startSearch', { searchText })
      // Save search to local store history (not a mutation, no reactivity cost).
      if ((saveToSearchHistory ?? true) && (query || searchKeywords.length > 0)) {
        const history = [...searchKeywords]
        if (query) {
          history.push(query)
        }
        statusMonitorSearchHistory.add(history.join('&'))
      }
      return dispatch('fetchPage', queryParameters)
    }
  }
}
